import React, { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  CheckmarkCircle02Icon,
  Cancel01Icon,
  AlertDiamondIcon,
  InformationCircleIcon,
} from '@hugeicons/core-free-icons';
import i18n from '../i18n/config';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
}

const TOAST_CONFIG: Record<
  ToastVariant,
  { border: string; icon: typeof CheckmarkCircle02Icon; iconBg: string; iconText: string }
> = {
  success: {
    border: 'border-s-4 border-secondary',
    icon: CheckmarkCircle02Icon,
    iconBg: 'bg-secondary/20',
    iconText: 'text-secondary',
  },
  error: {
    border: 'border-s-4 border-surface-error-primary',
    icon: Cancel01Icon,
    iconBg: 'bg-surface-error-lighter',
    iconText: 'text-surface-error-primary',
  },
  warning: {
    border: 'border-s-4 border-amber-500',
    icon: AlertDiamondIcon,
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-800',
  },
  info: {
    border: 'border-s-4 border-primary',
    icon: InformationCircleIcon,
    iconBg: 'bg-primary/10',
    iconText: 'text-primary',
  },
};

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 4L4 12M4 4l8 8" />
    </svg>
  );
}

function ToastUI({
  item,
  isRtl,
  onClose,
}: {
  item: ToastItem;
  isRtl: boolean;
  onClose: () => void;
}) {
  const config = TOAST_CONFIG[item.variant];
  const Icon = config.icon;
  const dismissLabel = i18n.t('toast.dismiss', { defaultValue: 'Dismiss notification' });
  return (
    <div
      dir={isRtl ? 'rtl' : 'ltr'}
      className={`flex min-w-[320px] max-w-md flex-row items-start gap-3 rounded-lg bg-surface-cards ${config.border} p-4 shadow-lg ring-1 ring-black/5 font-sans`}
      role="alert"
    >
      <div className="flex min-w-0 flex-1 flex-row items-start gap-3">
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${config.iconBg} ${config.iconText}`}
        >
          <HugeiconsIcon icon={Icon} size={18} />
        </span>
        <div className="min-w-0 flex-1 pt-0.5 text-start">
          <p className="text-sm font-semibold text-primary">{item.title}</p>
          {item.description ? (
            <p className="mt-0.5 text-sm text-primary/70">{item.description}</p>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="shrink-0 rounded p-1 text-primary/50 transition-colors hover:bg-surface-gray-light hover:text-primary/70 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
        aria-label={dismissLabel}
      >
        <CloseIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

type ToastContextValue = {
  addToast: (opts: { title: string; description?: string; variant?: ToastVariant }) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

let globalToastRef: ToastContextValue['addToast'] | null = null;

export function setGlobalToastHandler(handler: ToastContextValue['addToast'] | null) {
  globalToastRef = handler;
}

export function showGlobalToast(opts: {
  title: string;
  description?: string;
  variant?: ToastVariant;
}) {
  if (globalToastRef) globalToastRef(opts);
}

function useRtlSync() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const onLang = () => setTick((n) => n + 1);
    i18n.on('languageChanged', onLang);
    return () => {
      i18n.off('languageChanged', onLang);
    };
  }, []);
  return i18n.dir() === 'rtl';
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const isRtl = useRtlSync();

  const addToast = useCallback(
    (opts: { title: string; description?: string; variant?: ToastVariant }) => {
      const rawTitle = (opts.title ?? '').trim();
      const rawDesc = (opts.description ?? '').trim();
      const title =
        rawTitle ||
        i18n.t('toast.fallbackTitle', { defaultValue: 'Notification' });
      const description = rawDesc || undefined;
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      setToasts((prev) => [
        ...prev,
        {
          id,
          variant: opts.variant ?? 'info',
          title,
          description,
        },
      ]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 5000);
    },
    [],
  );

  React.useEffect(() => {
    globalToastRef = addToast;
    return () => {
      globalToastRef = null;
    };
  }, [addToast]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const slideX = isRtl ? -24 : 24;

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div
        className="fixed end-4 top-4 z-[1000] flex flex-col gap-2 font-sans"
        aria-live="polite"
      >
        <AnimatePresence initial={false}>
          {toasts.map((item) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, x: slideX }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: slideX }}
              transition={{ type: 'spring', stiffness: 420, damping: 32 }}
            >
              <ToastUI item={item} isRtl={isRtl} onClose={() => removeToast(item.id)} />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

/**
 * Inline callout using the same surface tokens as {@link ToastUI} (DESIGN_SYSTEM §9.1).
 * For persistent in-card status, not viewport toasts.
 */
export function InlineToastCallout({
  variant,
  title,
  description,
  contentDir,
  className = '',
}: {
  variant: ToastVariant;
  title?: ReactNode;
  description?: ReactNode;
  /** Reading direction for title + description wrapper (`text-start` + logical alignment). */
  contentDir?: 'rtl' | 'ltr';
  className?: string;
}) {
  const hasTitle = title != null && title !== '';
  const hasDesc = description != null && description !== '';
  if (!hasTitle && !hasDesc) return null;

  const config = TOAST_CONFIG[variant];
  const Icon = config.icon;
  return (
    <div
      className={['flex w-full gap-3 rounded-lg bg-surface-cards', config.border, 'p-4 shadow-lg ring-1 ring-black/5 font-sans', className]
        .filter(Boolean)
        .join(' ')}
      role="status"
      aria-live="polite"
    >
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${config.iconBg} ${config.iconText}`}
        aria-hidden
      >
        <HugeiconsIcon icon={Icon} size={18} />
      </span>
      <div className="flex-1 min-w-0 pt-0.5 text-start" dir={contentDir}>
        {hasTitle ? <p className="text-sm font-semibold text-primary m-0">{title}</p> : null}
        {hasDesc ? (
          <p className={`text-sm text-primary/70 m-0 leading-relaxed ${hasTitle ? 'mt-0.5' : ''}`}>{description}</p>
        ) : null}
      </div>
    </div>
  );
}

/** Success variant — same tokens as success {@link ToastUI}. */
export function InlineSuccessCallout({
  title,
  description,
  contentDir,
  className = '',
}: {
  title: ReactNode;
  description?: ReactNode;
  contentDir?: 'rtl' | 'ltr';
  className?: string;
}) {
  return (
    <InlineToastCallout
      variant="success"
      title={title}
      description={description}
      contentDir={contentDir}
      className={className}
    />
  );
}
